import i18n from 'i18next';
import { useCallback, useRef } from 'react';
import { Platform } from 'react-native';
import { Toast } from 'react-native-toast-notifications';

import { showSubmitErrorToast } from '@components/CustomToast/showSubmitErrorToast';
import { TaskStatus, useGetTaskLazyQuery } from '@graphql/generated';
import { canEditTaskThirdLevel } from '@hooks/useTaskMembership';
import { logError } from '@src/utils/logNonFatalError';

export const CANNOT_COMPLETE_TASK_ERROR_KEY = 'cannotCompleteTaskRole';

/**
 * The generic error surfaces (a small grey toast on web, a blocking Alert on
 * native) are easy to miss or heavy-handed for this, so each platform uses a
 * toast that is already used elsewhere: web the StatusToast error variant
 * that failed form submits use, native the plain bottom toast used for
 * "Priority Updated" in TaskFormStack.
 */
const notifyCannotCompleteTask = () => {
  const message = i18n.t(`shared:errors.${CANNOT_COMPLETE_TASK_ERROR_KEY}`);
  if (Platform.OS === 'web') {
    showSubmitErrorToast(message, { iconColor: 'alertRed' });
    return;
  }
  // Repeated taps would otherwise stack one toast per attempt.
  Toast.hideAll();
  Toast.show(message, { duration: 3000 });
};

/**
 * Re-fetches a task from the server right before it is completed, so the
 * decision is made on the user's current role rather than on a cached one
 * that may be stale (e.g. demoted to Viewer while the detail page was open).
 * The network result is written to the Apollo cache, so the open task detail
 * page refreshes with the latest data as a side effect.
 *
 * Resolves `true` when the caller should go on with completing the task.
 */
export const useEnsureCanCompleteTask = () => {
  const [getTask] = useGetTaskLazyQuery({ fetchPolicy: 'network-only' });
  // Guards against a double tap starting two refreshes (and two confirms).
  const inFlight = useRef(false);

  return useCallback(
    async (taskId: string): Promise<boolean> => {
      if (inFlight.current) return false;
      inFlight.current = true;
      // The refresh itself failing is not a reason to block the user: the
      // server still enforces the role on completeTask, so the mutation
      // stays the source of truth.
      const proceedAfterRefreshFailure = (errorMessage: string) => {
        logError({
          message: 'getTask refresh before complete failed',
          errorMessage,
          operationName: 'getTask',
          extra: [{ taskId }],
        });
        return true;
      };

      try {
        const { data, error } = await getTask({ variables: { id: taskId } });
        const latestTask = data?.getTask;

        if (!latestTask) {
          return proceedAfterRefreshFailure(
            error?.message ?? 'No task returned'
          );
        }

        // completeTask toggles: if someone else completed the task meanwhile,
        // completing it "again" would reopen it. The refreshed page already
        // shows it as completed, so there is nothing left to do. Both signals
        // are checked, as the detail pages do: completedAt is nullable, so a
        // COMPLETED status can arrive without a timestamp.
        if (
          latestTask.completedAt ||
          latestTask.status === TaskStatus.Completed
        ) {
          return false;
        }

        if (!canEditTaskThirdLevel(latestTask)) {
          notifyCannotCompleteTask();
          return false;
        }

        return true;
      } catch (err) {
        return proceedAfterRefreshFailure(
          err instanceof Error ? err.message : String(err)
        );
      } finally {
        inFlight.current = false;
      }
    },
    [getTask]
  );
};
